import { useState, useEffect, useCallback } from "react";
import { authFetch } from "../lib/authFetch";
import { usePortailAuth } from "../lib/auth-context";
import { usePortailRouter } from "../lib/router-context";
import {
  Bell, FileSignature, LifeBuoy, ShoppingCart, ChevronRight,
  FileText, File, Film, Image as ImageIcon, FileType, Download,
  TrendingUp, Target, Loader2, BookOpen, Tag, Sparkles, Mail, Phone, Users, Bot, MessageCircle,
} from "lucide-react";
import { ActuDetailPanel } from "../components/ActuDetailPanel";
import type { ActualiteUnified as ActuType } from "../components/ActuDetailPanel";

// ── Types ───────────────────────────────────────────────────────────────────
type ContactInfo = {
  nom: string; prenom: string; fonction: string | null;
  email: string | null; telephone: string | null; avatar_url: string | null;
} | null;

type ActualiteUnified = ActuType;

type DashboardData = {
  revendeur: {
    companyName: string;
    logoUrl: string | null;
    couleurPrimaire: string;
    couleurSecondaire: string;
    resellerNumber: string | null;
    puduAgentCode: string | null;
    contractStatus: string;
    contractStartDate: string | null;
    contractEndDate: string | null;
  };
  kpis: {
    notifications: number;
    devis_en_cours: number;
    tickets_ouverts: number;
    commandes_en_cours: number;
    robots_actifs: number;
  };
  actualites: ActualiteUnified[];
  documentsRecents: DocRecent[];
  performance: Performance | null;
  interlocuteurs?: {
    commercial: ContactInfo;
    comptabilite: ContactInfo;
    devis_commandes: ContactInfo;
    administratif: ContactInfo;
  };
};
type DocRecent = {
  id: string; titre: string; typeFichier: string | null;
  categorieNom: string | null; createdAt: string; isNew: boolean;
  signedUrl: string | null;
};
type Performance = {
  objectif: number; vendus: number; taux: number;
  annee: number; label: string | null;
  dateDebut: string | null; dateFin: string | null;
};

// ── Helpers ─────────────────────────────────────────────────────────────────
function formatDate(d?: string | null) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}
function formatDateShort(d?: string | null) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}
function fmtCurrency(v: string | null | number) {
  if (v === null || v === undefined) return "—";
  const n = typeof v === "string" ? parseFloat(v) : v;
  return isNaN(n) ? "—" : n.toLocaleString("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0 });
}
function getLabelContrat(cs: string) {
  const map: Record<string, string> = {
    revendeur: "Revendeur", revendeur_premium: "Revendeur Premium",
    sous_distributeur: "Sous-distributeur", apporteur_affaires: "Apporteur d'affaires",
  };
  return map[cs] || cs;
}
function getProgressColor(taux: number) {
  if (taux >= 100) return "bg-violet-500";
  if (taux >= 75) return "bg-green-500";
  if (taux >= 50) return "bg-amber-400";
  return "bg-red-500";
}
function FileIcon({ type }: { type?: string | null }) {
  if (type === "pdf") return <FileText className="w-5 h-5 text-red-500" />;
  if (type === "word") return <File className="w-5 h-5 text-blue-500" />;
  if (type === "video") return <Film className="w-5 h-5 text-violet-500" />;
  if (type === "image") return <ImageIcon className="w-5 h-5 text-green-500" />;
  return <FileType className="w-5 h-5 text-muted-foreground" />;
}
function Initiales({ name, primary, secondary }: { name: string; primary: string; secondary: string }) {
  const parts = name.split(" ");
  const ini = (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
  return (
    <div
      className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-xl shrink-0"
      style={{ background: `linear-gradient(135deg, ${primary}, ${secondary})` }}
    >
      {ini.toUpperCase()}
    </div>
  );
}

// ── Contact Card ─────────────────────────────────────────────────────────
const ROLE_LABELS: Record<string, string> = {
  admin: "Administrateur",
  planification: "Planification",
  technicien: "Technicien",
  commercial: "Commercial",
  administration: "Administration",
};

function ContactAvatar({ contact, size = 56 }: { contact: NonNullable<ContactInfo>; size?: number }) {
  if (contact.avatar_url) {
    return (
      <img
        src={contact.avatar_url}
        alt={`${contact.prenom} ${contact.nom}`}
        className="rounded-full object-cover shrink-0 border border-border/40"
        style={{ width: size, height: size }}
      />
    );
  }
  const ini = (contact.prenom?.[0] || "") + (contact.nom?.[0] || "");
  const colors = ["#2563eb", "#7c3aed", "#059669", "#d97706", "#dc2626", "#0891b2"];
  const colorIdx = (contact.nom.charCodeAt(0) || 0) % colors.length;
  return (
    <div
      className="rounded-full flex items-center justify-center text-white font-bold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.35, background: colors[colorIdx] }}
    >
      {ini.toUpperCase() || "?"}
    </div>
  );
}

function ContactCard({ titre, contact }: { titre: string; contact: ContactInfo }) {
  if (!contact) return null;
  return (
    <div className="bg-card border border-border/50 rounded-xl p-4 flex flex-col gap-3 hover:shadow-sm transition-shadow">
      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">{titre}</p>
      <div className="flex items-center gap-3">
        <ContactAvatar contact={contact} size={52} />
        <div className="min-w-0">
          <p className="font-semibold text-sm leading-tight">{contact.prenom} {contact.nom.toUpperCase()}</p>
          {contact.fonction && (
            <p className="text-xs text-muted-foreground mt-0.5">{ROLE_LABELS[contact.fonction] ?? contact.fonction}</p>
          )}
        </div>
      </div>
      <div className="space-y-1.5 border-t border-border/40 pt-2.5">
        {contact.email && (
          <a
            href={`mailto:${contact.email}`}
            className="flex items-center gap-2 text-xs text-foreground/80 hover:text-primary transition-colors group"
            data-testid={`contact-email-${contact.email}`}
          >
            <Mail className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary shrink-0" />
            <span className="truncate">{contact.email}</span>
          </a>
        )}
        {contact.telephone && (
          <a
            href={`tel:${contact.telephone}`}
            className="flex items-center gap-2 text-xs text-foreground/80 hover:text-primary transition-colors group"
            data-testid={`contact-tel-${contact.telephone}`}
          >
            <Phone className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary shrink-0" />
            <span>{contact.telephone}</span>
          </a>
        )}
      </div>
    </div>
  );
}

// ── Dashboard principal ──────────────────────────────────────────────────────
export default function Dashboard() {
  const { portailUser } = usePortailAuth();
  const { navigate } = usePortailRouter();

  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedActu, setSelectedActu] = useState<ActualiteUnified | null>(null);
  const [chatsEnAttente, setChatsEnAttente] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch("/api/portail/dashboard");
      if (r.ok) setData(await r.json());
    } catch {}
    setLoading(false);
  }, []);

  const fetchChatsCount = useCallback(async () => {
    try {
      const r = await authFetch("/api/portail/chats/awaiting-count");
      if (r.ok) {
        const json = await r.json();
        setChatsEnAttente(json.count ?? 0);
      }
    } catch {}
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    fetchChatsCount();
    const interval = setInterval(fetchChatsCount, 60_000);
    return () => clearInterval(interval);
  }, [fetchChatsCount]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground gap-2">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span className="text-sm">Chargement...</span>
      </div>
    );
  }

  if (!data) return null;

  const { revendeur, kpis, actualites, documentsRecents, performance } = data;
  const primary = revendeur.couleurPrimaire || "#2563eb";
  const secondary = revendeur.couleurSecondaire || "#1e40af";
  const dateJour = new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const kpiCards = [
    {
      label: "Notifications", value: kpis.notifications, icon: Bell, color: "bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400",
      href: "/ressources", testId: "kpi-notifications",
    },
    {
      label: "Devis en cours", value: kpis.devis_en_cours, icon: FileSignature, color: "bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400",
      href: "/devis", testId: "kpi-devis",
    },
    {
      label: "Tickets SAV ouverts", value: kpis.tickets_ouverts, icon: LifeBuoy, color: "bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400",
      href: "/tickets", testId: "kpi-tickets",
    },
    {
      label: "Commandes en cours", value: kpis.commandes_en_cours, icon: ShoppingCart, color: "bg-violet-50 dark:bg-violet-950/30 text-violet-600 dark:text-violet-400",
      href: "/commandes", testId: "kpi-commandes",
    },
    {
      label: "Robots actifs", value: kpis.robots_actifs ?? 0, icon: Bot, color: "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400",
      href: "/robots", testId: "kpi-robots",
    },
    {
      label: "Chats en attente", value: chatsEnAttente, icon: MessageCircle, color: "bg-sky-50 dark:bg-sky-950/30 text-sky-600 dark:text-sky-400",
      href: "/chats", testId: "kpi-chats-attente", alertOnPositive: true,
    },
  ];

  return (
    <div className="flex-1 overflow-y-auto">
      {/* ── Bandeau bienvenue ─────────────────────────────────────────── */}
      <div
        className="w-full px-6 py-6 sm:py-8"
        style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` }}
        data-testid="bandeau-bienvenue"
      >
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {/* Logo ou initiales */}
          <div className="shrink-0">
            {revendeur.logoUrl ? (
              <img
                src={revendeur.logoUrl}
                alt={revendeur.companyName}
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl object-contain bg-white/10 p-1"
                data-testid="img-logo-revendeur"
              />
            ) : (
              <Initiales name={revendeur.companyName} primary="rgba(255,255,255,0.25)" secondary="rgba(255,255,255,0.1)" />
            )}
          </div>

          {/* Texte */}
          <div className="flex-1 min-w-0 text-white">
            <h1 className="text-xl sm:text-2xl font-bold leading-tight">
              Bonjour {portailUser?.prenom || "Partenaire"} — Bienvenue dans votre espace{" "}
              <span className="opacity-90">{revendeur.companyName}</span>
            </h1>
            <p className="text-sm opacity-80 mt-0.5 capitalize">{dateJour}</p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
              {(revendeur.resellerNumber || revendeur.puduAgentCode) && (
                <span className="text-xs opacity-75 bg-white/10 px-2 py-0.5 rounded-full">
                  N° partenaire : {revendeur.resellerNumber || revendeur.puduAgentCode}
                </span>
              )}
              <span className="text-xs opacity-75 bg-white/10 px-2 py-0.5 rounded-full">
                {getLabelContrat(revendeur.contractStatus)}
              </span>
              {(revendeur.contractStartDate || revendeur.contractEndDate) && (
                <span className="text-xs opacity-70">
                  Contrat : {revendeur.contractStartDate ? `du ${formatDateShort(revendeur.contractStartDate)}` : ""}
                  {revendeur.contractEndDate ? ` au ${formatDateShort(revendeur.contractEndDate)}` : ""}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Corps ──────────────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-8">

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4" data-testid="section-kpis">
          {kpiCards.map(card => (
            <button
              key={card.label}
              onClick={() => navigate(card.href)}
              className="group bg-card border border-border/50 rounded-xl p-4 sm:p-5 flex flex-col items-start gap-3 hover:shadow-md hover:border-border transition-all text-left w-full"
              data-testid={card.testId}
            >
              <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center ${card.color}`}>
                <card.icon className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <div>
                <p className={`text-2xl sm:text-3xl font-bold leading-none ${(card as any).alertOnPositive && card.value > 0 ? "text-red-600 dark:text-red-400" : "text-foreground"}`}>
                  {card.value}
                </p>
                <p className="text-xs text-muted-foreground mt-1 leading-tight">{card.label}</p>
              </div>
            </button>
          ))}
        </div>

        {/* ── Actualités (Nouveautés + Promotions + Informations) ─────── */}
        {actualites.length > 0 && (
          <section data-testid="section-actualites">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                Actualités
              </h2>
              <button
                onClick={() => navigate("/actualites")}
                className="text-xs text-primary hover:underline flex items-center gap-0.5"
                data-testid="btn-tout-voir-actualites"
              >
                Tout voir <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {actualites.map(actu => {
                const isPromo = actu.type === 'promotion';
                const isInfo = actu.type === 'information';
                const economie = isPromo && actu.prixNormal && actu.prixPromo
                  ? parseFloat(actu.prixNormal) - parseFloat(actu.prixPromo)
                  : null;
                const badge = isPromo
                  ? { label: "Promotion", cls: "bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400" }
                  : isInfo
                  ? { label: "Information", cls: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300" }
                  : { label: "Nouveauté", cls: "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400" };
                const displayTitle = isPromo ? (actu.produitNom || actu.titre) : actu.titre;
                return (
                  <div
                    key={actu.id}
                    className="bg-card border border-border/50 rounded-xl overflow-hidden flex flex-col hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => setSelectedActu(actu)}
                    data-testid={`card-actualite-${actu.id}`}
                  >
                    {actu.imageUrl ? (
                      <img src={actu.imageUrl} alt={displayTitle} className="w-full h-36 object-cover" />
                    ) : (
                      <div className="w-full h-36 flex items-center justify-center bg-muted/40">
                        {isPromo
                          ? <Tag className="w-8 h-8 text-muted-foreground/30" />
                          : <Sparkles className="w-8 h-8 text-muted-foreground/30" />}
                      </div>
                    )}
                    <div className="p-3 flex flex-col flex-1 gap-2">
                      <span className={`self-start text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${badge.cls}`}>
                        {badge.label}
                      </span>
                      <p className="font-semibold text-sm leading-snug line-clamp-2">{displayTitle}</p>
                      {actu.accroche && (
                        <p className="text-xs text-muted-foreground line-clamp-2 flex-1">{actu.accroche}</p>
                      )}
                      {isPromo && (actu.prixNormal || actu.prixPromo) && (
                        <div className="flex items-center gap-2 flex-wrap">
                          {actu.prixNormal && (
                            <span className="text-sm text-muted-foreground line-through">{fmtCurrency(actu.prixNormal)}</span>
                          )}
                          {actu.prixPromo && (
                            <span className="text-base font-bold text-orange-600 dark:text-orange-400">{fmtCurrency(actu.prixPromo)}</span>
                          )}
                          {economie !== null && economie > 0 && (
                            <span className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400 px-1.5 py-0.5 rounded-full font-medium">
                              -{fmtCurrency(economie)}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="flex items-center justify-between pt-1 mt-auto">
                        <span className="text-xs text-muted-foreground">{formatDate(actu.createdAt)}</span>
                        <span
                          className="text-xs text-primary font-medium"
                          data-testid={`btn-savoir-plus-${actu.id}`}
                        >
                          En savoir +
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Documents récents ──────────────────────────────────────── */}
        <section data-testid="section-documents-recents">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-base flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-amber-500" />
              Documents récents
            </h2>
            <button onClick={() => navigate("/ressources")} className="text-xs text-primary hover:underline flex items-center gap-0.5">
              Voir tous les documents <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {documentsRecents.length === 0 ? (
            <div className="bg-muted/30 rounded-xl p-6 text-center text-sm text-muted-foreground">
              Aucun document disponible pour l'instant.
            </div>
          ) : (
            <div className="space-y-2">
              {documentsRecents.map(doc => (
                <div
                  key={doc.id}
                  className="bg-card border border-border/50 rounded-xl p-3 flex items-center gap-3 hover:shadow-sm transition-shadow"
                  data-testid={`doc-recent-${doc.id}`}
                >
                  <div className="shrink-0"><FileIcon type={doc.typeFichier} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">{doc.titre}</span>
                      {doc.isNew && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400 font-semibold shrink-0">
                          Nouveau
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {doc.categorieNom && (
                        <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full">{doc.categorieNom}</span>
                      )}
                      <span className="text-xs text-muted-foreground">{formatDate(doc.createdAt)}</span>
                    </div>
                  </div>
                  {doc.signedUrl ? (
                    <a
                      href={doc.signedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 flex items-center gap-1 px-3 py-1.5 text-xs border border-border rounded-lg hover:bg-muted/50 transition-colors text-muted-foreground"
                      data-testid={`btn-dl-recent-${doc.id}`}
                    >
                      <Download className="w-3.5 h-3.5" />
                      Télécharger
                    </a>
                  ) : (
                    <span className="text-xs text-muted-foreground italic shrink-0">Indisponible</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── Performance ────────────────────────────────────────────── */}
        {performance && performance.objectif > 0 && (
          <section data-testid="section-performance">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-500" />
                Ma performance {performance.annee}
                {performance.label && <span className="text-xs text-muted-foreground font-normal">— {performance.label}</span>}
              </h2>
              <button onClick={() => navigate("/societe")} className="text-xs text-primary hover:underline flex items-center gap-0.5">
                Voir le détail <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="bg-card border border-border/50 rounded-xl p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-green-50 dark:bg-green-950/30 flex items-center justify-center shrink-0">
                    <Target className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Objectif annuel</p>
                    <p className="font-bold text-lg leading-tight">{performance.objectif} robots</p>
                  </div>
                </div>
                <div className="hidden sm:block w-px h-10 bg-border" />
                <div>
                  <p className="text-xs text-muted-foreground">Vendus</p>
                  <p className="font-bold text-lg leading-tight">{performance.vendus} robots</p>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs text-muted-foreground">Progression</span>
                    <span className={`text-sm font-bold ${performance.taux >= 100 ? "text-violet-600" : performance.taux >= 75 ? "text-green-600" : performance.taux >= 50 ? "text-amber-500" : "text-red-500"}`}>
                      {Math.round(performance.taux)}%
                    </span>
                  </div>
                  <div className="w-full h-2.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${getProgressColor(performance.taux)}`}
                      style={{ width: `${Math.min(100, performance.taux)}%` }}
                      data-testid="progress-performance"
                    />
                  </div>
                  {performance.dateDebut && performance.dateFin && (
                    <p className="text-xs text-muted-foreground mt-1.5">
                      Contrat du {formatDateShort(performance.dateDebut)} au {formatDateShort(performance.dateFin)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ── Interlocuteurs Jobotto ─────────────────────────────────── */}
        {data.interlocuteurs && (
          Object.values(data.interlocuteurs).some(c => c !== null)
        ) && (
          <section data-testid="section-interlocuteurs">
            <div className="flex items-center gap-2 mb-3">
              <h2 className="font-semibold text-base flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" />
                Vos interlocuteurs Jobotto
              </h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <ContactCard
                titre="Votre contact commercial"
                contact={data.interlocuteurs.commercial}
              />
              <ContactCard
                titre="Votre contact comptabilité"
                contact={data.interlocuteurs.comptabilite}
              />
              <ContactCard
                titre="Vos devis et commandes"
                contact={data.interlocuteurs.devis_commandes}
              />
              <ContactCard
                titre="Questions administratives"
                contact={data.interlocuteurs.administratif}
              />
            </div>
          </section>
        )}

      </div>

      {selectedActu && (
        <ActuDetailPanel actu={selectedActu} onClose={() => setSelectedActu(null)} />
      )}
    </div>
  );
}
